import type { Editor } from '@tiptap/react';
import type { QueryClient } from '@tanstack/react-query';
import { updateBlockContentInCache } from './updateBlockContentInCache';
import { resolveBlockId } from './editorBlockUtils';
import { apiDocumentServiceRepository } from '../../../../../services/repositories/implementations/ApiDocumentServiceRepository.ts';
import type { JSONCommentDto } from '@proposify/document-service-api-schema';

interface CreateThreadInEditorPreviewParams {
  activeEditor: Editor;
  blockId: string;
  threadId: string;
  contentId: string;
  queryClient: QueryClient;
}

interface UpdateThreadInEditorPreviewParams {
  activeEditor: Editor;
  threadId: string;
  isResolved: boolean;
  contentId: string;
  queryClient: QueryClient;
}

interface DeleteThreadInEditorPreviewParams {
  activeEditor: Editor;
  threadId: string;
  contentId: string;
  queryClient: QueryClient;
}

export const createThreadInEditorPreview = async ({
  activeEditor,
  blockId,
  threadId,
  contentId,
  queryClient,
}: CreateThreadInEditorPreviewParams): Promise<JSONCommentDto | null> => {
  const payload = {
    xPos: activeEditor.state.selection.from,
    yPos: activeEditor.state.selection.to,
    comment: { threadId, isResolved: false, blockId },
  };
  const response = await apiDocumentServiceRepository.createComment(blockId, payload);

  if (response && contentId) {
    updateBlockContentInCache(queryClient, contentId, blockId, response);
  }

  return response;
};

export const updateThreadInEditorPreview = async ({
  activeEditor,
  threadId,
  isResolved,
  contentId,
  queryClient,
}: UpdateThreadInEditorPreviewParams): Promise<JSONCommentDto | null> => {
  const blockId = resolveBlockId(activeEditor);

  if (!blockId) {
    return null;
  }
  const payload = { threadId, isResolved, blockId };

  const response = await apiDocumentServiceRepository.updateComment(blockId, payload);

  if (response && contentId) {
    updateBlockContentInCache(queryClient, contentId, blockId, response);
  }

  return response;
};

export const deleteThreadInEditorPreview = async ({
  activeEditor,
  threadId,
  contentId,
  queryClient,
}: DeleteThreadInEditorPreviewParams): Promise<JSONCommentDto | null> => {
  const blockId = resolveBlockId(activeEditor);

  if (!blockId) {
    return null;
  }

  const response = await apiDocumentServiceRepository.deleteComment(blockId, threadId);

  if (response && blockId) {
    updateBlockContentInCache(queryClient, contentId, blockId, response);
  }

  return response;
};
